<template>
  <div v-if="preflightLoading" class="relative text-center">
    <DsIcon class="mr-2 text-blue-700" name="spinner-third" variant="duotone" spin size="2x" />
  </div>

  <div v-else-if="errorMsg">
    <DsAlert type="error" class="mb-5">{{ errorMsg }}</DsAlert>
    <DsButton
      variant="secondary"
      size="lg"
      type="submit"
      class="mt-6 w-full"
      @click="emit('switch-to-password')"
    >
      zum Login
    </DsButton>
  </div>

  <div v-else-if="hasTokenErrors" class="relative text-center">
    <DsAlert type="error" :label="errors.requestAccessToken[0]" />
    <DsButton
      variant="secondary"
      size="lg"
      type="submit"
      class="mt-6 w-full"
      @click="emit('switch-to-password')"
    >
      zum Login
    </DsButton>
  </div>

  <DsForm
    v-else
    :validation-errors="errors"
    class="flex flex-col gap-2"
    @submit.prevent
  >
    <div>
      Für {{ duzen('deine', 'Ihre') }} Anmeldung ist ein weiterer Faktor notwendig. {{ twoFactorLabels[twoFactorMode] }}
    </div>

    <DsFormGroup
      validation-name="answer"
      :label="twoFactorMode === TwoFactors.Birthdate ? 'Geburtsdatum' : 'Code/Recoverycode' "
      label-for="answer"
    >
      <DsInput
        id="answer"
        v-model="form.answer"
        :disabled="loading"
        type="default"
        size="lg"
        immediate
        autofocus
        :placeholder="twoFactorMode === TwoFactors.Birthdate? 'tt.mm.jjjj' : ''"
      />
    </DsFormGroup>

    <DsFormGroup
      v-if="form.deviceToken"
      class="mt-4"
      :disabled="loading"
      label="Gerät merken"
      help="Überspringt zukünftig diese Zwei-Faktor Abfrage auf diesem Gerät."
      inline
      label-for="tfa-remember-device-checkbox"
    >
      <DsCheckbox
        id="tfa-remember-device-checkbox"
        v-model="form.remember"
      />
    </DsFormGroup>

    <DsButton
      variant="primary"
      size="lg"
      type="submit"
      :handler="submit"
      class="mt-3 w-full"
    >
      Anmelden
    </DsButton>
  </DsForm>
</template>

<script setup lang="ts">
import { DsAlert, DsButton, DsCheckbox, DsForm, DsFormGroup, DsIcon, DsInput } from '@demvsystems/design-components';
import { useForm } from "@/api/lib/integration";
import { requestAccessTokenLogin } from "@/api/auth/authApi";
import { computed, onMounted, ref } from "vue";
import { getDeviceId, getDeviceName } from "@/application/utils/envInfo";
import { TwoFactors } from '@/api/auth/types';
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

// 1.1 token is invalid, check if old login exists
// 1.2 token is valid, check 2fa
// 2. logging in

interface Props {
  requestAccessToken: string,
}

const props = defineProps<Props>();

const { duzen } = useBaseDataStore();

const twoFactorLabels = {
  [TwoFactors.Fortitfy]: `Bitte ${duzen('gebe deinen', 'geben Sie Ihren')} Authenticator Code ein.`,
  [TwoFactors.Birthdate]: `Bitte ${duzen('bestätige dein', 'bestätigen Sie Ihr')} Geburtsdatum.`,
}

const emit = defineEmits(['logging-in', 'switch-to-password']);

const twoFactorMode = ref('');
const preflightLoading = ref(false);

const { errors, form, submit, errorMsg, loading } = useForm(
  requestAccessTokenLogin,
  {
    requestAccessToken: props.requestAccessToken,
    answer: undefined as string|undefined,
    deviceName: undefined as string|undefined,
    deviceToken: undefined as string|undefined,
    remember: false,
  },
    (data) => {
      if (data.twoFactor) {
        twoFactorMode.value = data.twoFactor;
        return;
      }

      emit('logging-in', data.accessToken, data.clientSecret);
    },
);

const hasTokenErrors = computed(() => errors.value.requestAccessToken ? errors.value.requestAccessToken.length > 0 : false);

onMounted(async () => {
  form.value.deviceName = (await getDeviceName()) ?? 'Generic device';
  form.value.deviceToken = await getDeviceId() ?? undefined;
  // initially send requestAccessToken to get 2FA challenge or login with accessToken
  preflightLoading.value = true;
  await submit();
  // enable tfa iput validation
  form.value.answer = '';
  preflightLoading.value = false;
});

defineExpose({ form, errors })
</script>
